import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<Card>` replaces `.panel`, `.kpi`, `.kb-card`,
 * `.aud-card`, `.rs-info-card`, `.clc`, and various page-specific card
 * shells in common-ui.css. Variant decides which visual bucket the card
 * fits: `panel` (grouped content with header + rows), `kpi` (stat tile),
 * `interactive` (clickable card with hover lift + top accent bar).
 *
 * The top-accent bar on interactive cards reproduces the `.kpi::before`
 * pattern via Tailwind's `before:` pseudo utility so we don't need a
 * dedicated CSS rule.
 */
const card = cva(
  "rounded-[10px] border bg-rc-surface border-rc-border text-rc-text-primary transition-all",
  {
    variants: {
      variant: {
        panel: "overflow-hidden",
        kpi: "px-[22px] py-[18px] relative overflow-hidden",
        interactive: [
          "px-[22px] py-[18px] relative overflow-hidden cursor-pointer",
          "before:content-[''] before:absolute before:inset-x-0 before:top-0 before:h-[3px]",
          "before:bg-rc-blue-500 before:opacity-0 before:transition-opacity",
          "hover:border-rc-blue-400 hover:bg-rc-surface-hover hover:-translate-y-px",
          "hover:shadow-[0_6px_24px_rgba(0,0,0,0.35)]",
          "hover:before:opacity-100",
        ].join(" "),
      },
      tone: {
        default: "",
        // Left accent bar for priority / status variants. Matches .clc.p1/.p2
        // and .tc.od/.bl/.st patterns from common-ui.css.
        critical: "border-l-[4px] border-l-rc-red",
        warn:     "border-l-[4px] border-l-rc-yellow",
        info:     "border-l-[4px] border-l-rc-accent",
        success:  "border-l-[4px] border-l-rc-green",
        plan:     "border-l-[4px] border-l-[#a78bfa]",
      },
    },
    defaultVariants: {
      variant: "panel",
      tone: "default",
    },
  },
);

const Card = forwardRef(function Card({ className, variant, tone, children, ...props }, ref) {
  return (
    <div ref={ref} className={cn(card({ variant, tone }), className)} {...props}>
      {children}
    </div>
  );
});

export default Card;
